// src/pages/TermsOfService.jsx
import React from 'react'
import { Link } from 'react-router-dom'
import Footer from '../components/Footer'

const EFFECTIVE = 'April 26, 2026'

export default function TermsOfService() {
  return (
    <div style={s.page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;900&family=DM+Mono:wght@300;400;500&display=swap');
        .hover-gold:hover { color: #c8a96e !important; }
      `}</style>

      <nav style={s.nav}>
        <Link to="/" style={s.navLogo}>
          <div style={s.navIcon}>
            <CrosshairIcon size={18} color="#0a0a0a" />
          </div>
          <div>
            <div style={s.navName}>DISDownrange</div>
            <div style={s.navSub}>by Dynamic Innovative Solutions</div>
          </div>
        </Link>
        <Link to="/" style={s.backLink} className="hover-gold">← Back to Home</Link>
      </nav>

      <main style={s.main}>
        <div style={s.header}>
          <div style={s.eyebrow}>Legal</div>
          <h1 style={s.title}>Terms of Service</h1>
          <div style={s.meta}>
            Last updated: {EFFECTIVE} · Effective: {EFFECTIVE}
          </div>
        </div>

        <Section title="1. Acceptance of Terms">
          <p>By creating an account or otherwise using DISDownrange ("the Service") you
            agree to these Terms of Service ("Terms") and the accompanying Privacy Policy.
            If you do not agree, do not use the Service. The Service is operated by Dynamic
            Innovative Solutions LLC ("DIS LLC"), a Missouri limited liability company.</p>
        </Section>

        <Section title="2. Description of Service">
          <p>DISDownrange is a Progressive Web App that provides:</p>
          <ul style={s.ul}>
            <li>A G1 point-mass ballistic trajectory calculator with barrel-length-corrected
              muzzle velocity.</li>
            <li>Terminal performance ratings (wound channel, penetration depth, barrier
              performance, FBI protocol flag) for a curated ammunition database.</li>
            <li>Real-time GPS-based weather integration via Open-Meteo for environmental
              corrections.</li>
            <li>Round comparison, ethical-range, and (Elite tier) shot-logging features.</li>
          </ul>
          <p>The Service is informational. It is not a measurement instrument, not a fire
            control system, and not a substitute for live-fire verification.</p>
        </Section>

        <Section title="3. Eligibility and Account Registration">
          <p>To use the Service you must:</p>
          <ul style={s.ul}>
            <li>Be at least 18 years old.</li>
            <li>Be lawfully entitled to own and use firearms in your jurisdiction.</li>
            <li>Provide accurate registration information and keep it current.</li>
            <li>Maintain the security of your account credentials. You are responsible for
              all activity under your account.</li>
          </ul>
          <p>We may suspend or terminate accounts that violate these requirements.</p>
        </Section>

        <Section title="4. Subscription Tiers and Billing">
          <p>The Service is offered in three tiers:</p>
          <ul style={s.ul}>
            <li><b>Free</b> — basic ballistic calculator and limited ammo database.</li>
            <li><b>Pro</b> — $34.99/year. Full terminal performance data, GPS weather,
              ethical-range calculator, and round comparison.</li>
            <li><b>Elite</b> — $69.99/year. Everything in Pro plus shot logging, terrain
              presets, and priority ammo updates.</li>
          </ul>
          <p>Current pricing is displayed at checkout. We may update pricing for new
            subscribers from time to time; existing subscribers are not affected by price
            changes during their current paid term.</p>
          <p>Paid subscriptions are billed annually in advance through Stripe. Subscriptions
            renew automatically at the end of each billing period unless cancelled. You may
            cancel at any time from your account settings; access to paid features continues
            through the end of the period you have already paid for.</p>
        </Section>

        <Section title="5. Refund Policy">
          <p>You may request a full refund within 14 days of any annual subscription charge.
            After 14 days, annual subscriptions are non-refundable. DIS LLC may, at its sole
            discretion, issue prorated refunds in cases of extended Service unavailability or
            other extraordinary circumstances. To request a refund, email{' '}
            <a href="mailto:help@disdownrange.org" style={s.inlineLink} className="hover-gold">
              help@disdownrange.org
            </a>.</p>
        </Section>

        <Section title="6. Ballistic Data Disclaimer — Read Carefully">
          <p style={s.warn}>
            All ballistic data, terminal performance ratings, and recommendations provided
            by DISDownrange are <b>informational only</b>. Ratings are based on published
            manufacturer specifications, third-party test data, and modeled calculations.
            They are <b>not laboratory-measured</b> against your specific equipment or
            environment.
          </p>
          <p>You are solely responsible for every shot you take. DIS LLC is not liable for:</p>
          <ul style={s.ul}>
            <li>Missed shots, wounded game, or any loss of game during hunting.</li>
            <li>Damage to firearms, optics, or other equipment.</li>
            <li>Injury to yourself, other persons, or animals.</li>
            <li>Any other consequence — direct, indirect, incidental, or consequential —
              of acting on data displayed by the Service.</li>
          </ul>
          <p>You must verify all ballistic data against your own firearm, ammunition, optics,
            and environmental conditions before relying on it in the field. Always follow
            applicable firearms safety rules and the laws of your jurisdiction.</p>
        </Section>

        <Section title="7. Affiliate Disclosure">
          <p>The Service contains affiliate links to ammunition retailers, optics
            manufacturers, and related vendors, including links served through the AvantLink
            affiliate network and other affiliate programs. DIS LLC may earn a commission on
            qualifying purchases made through these links. These commissions do not affect
            the price you pay and do not influence ballistic ratings or recommendations.
            Affiliate cookies set by these networks are described in our Privacy Policy.</p>
        </Section>

        <Section title="8. User-Generated Content">
          <p>Future versions of the Service may allow you to submit ammunition entries,
            chronograph data, shot-log corrections, or other content ("Submissions"). By
            making a Submission you:</p>
          <ul style={s.ul}>
            <li>Grant DIS LLC a worldwide, royalty-free, perpetual, sublicensable license to
              use, reproduce, modify, and incorporate the Submission into the Service and
              the curated database.</li>
            <li>Warrant that you have the right to submit the content and that it does not
              infringe any third party's rights.</li>
            <li>Acknowledge that DIS LLC may reject, edit, or remove any Submission at its
              sole discretion.</li>
          </ul>
        </Section>

        <Section title="9. Acceptable Use">
          <p>You agree not to:</p>
          <ul style={s.ul}>
            <li>Scrape, mass-download, or systematically extract the ammunition database or
              any other content from the Service.</li>
            <li>Resell, sublicense, or commercially redistribute Service content or data.</li>
            <li>Reverse engineer, decompile, or attempt to derive the source code of the
              Service or its ballistic engine.</li>
            <li>Interfere with the operation of the Service, attempt to gain unauthorized
              access, or probe for vulnerabilities outside a coordinated disclosure.</li>
            <li>Use the Service to facilitate any unlawful activity.</li>
          </ul>
        </Section>

        <Section title="10. Intellectual Property">
          <p>DIS LLC owns all rights, title, and interest in the Service, including the
            DISDownrange name and logo, the trajectory engine, the terminal performance
            rating methodology, the curated ammunition database, the user interface, and all
            associated software and documentation. Except for the limited license to use the
            Service granted by these Terms, no rights are transferred to you.</p>
        </Section>

        <Section title="11. Limitation of Liability">
          <p>To the maximum extent permitted by law, DIS LLC's total cumulative liability to
            you for any and all claims arising out of or relating to the Service is limited
            to the greater of (a) the amount you paid DIS LLC for the Service in the twelve
            (12) months preceding the event giving rise to the claim, or (b) one hundred US
            dollars ($100). DIS LLC is not liable for any indirect, incidental, special,
            consequential, exemplary, or punitive damages, including loss of profits, data,
            or goodwill, even if advised of the possibility of such damages.</p>
        </Section>

        <Section title="12. Indemnification">
          <p>You agree to indemnify and hold DIS LLC, its members, officers, and agents
            harmless from any claim or demand — including reasonable attorneys' fees — made
            by a third party arising out of your use of the Service, your violation of these
            Terms, or your violation of any law or third-party right.</p>
        </Section>

        <Section title="13. Governing Law and Disputes">
          <p>These Terms are governed by the laws of the State of Missouri, without regard
            to its conflict-of-law principles. Any dispute arising under these Terms shall be
            resolved in the state or federal courts located in Missouri, and you consent to
            personal jurisdiction in those courts.</p>
        </Section>

        <Section title="14. Changes to Terms">
          <p>We may update these Terms from time to time. Material changes will be announced
            by email to the address on your account and reflected by an updated "Last
            updated" date at the top of this page. If you do not agree to the revised Terms,
            your sole remedy is to cancel your subscription and discontinue use of the
            Service.</p>
        </Section>

        <Section title="15. Termination">
          <p>You may close your account at any time. We may suspend or terminate your access
            to the Service if you violate these Terms or applicable law. Sections that by
            their nature should survive termination — including disclaimers, limitations of
            liability, intellectual property, indemnification, and governing law — will
            survive.</p>
        </Section>

        <Section title="16. Contact">
          <p>
            Dynamic Innovative Solutions LLC<br/>
            Winona, MO 65588<br/>
            Email:{' '}
            <a href="mailto:help@disdownrange.org" style={s.inlineLink} className="hover-gold">
              help@disdownrange.org
            </a>
          </p>
        </Section>
      </main>

      <Footer />
    </div>
  )
}

function Section({ title, children }) {
  return (
    <section style={s.sect}>
      <h2 style={s.h2}>{title}</h2>
      <div style={s.sectBody}>{children}</div>
    </section>
  )
}

function CrosshairIcon({ size = 22, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round">
      <circle cx="12" cy="12" r="7"/>
      <line x1="12" y1="2" x2="12" y2="5"/>
      <line x1="12" y1="19" x2="12" y2="22"/>
      <line x1="2" y1="12" x2="5" y2="12"/>
      <line x1="19" y1="12" x2="22" y2="12"/>
      <circle cx="12" cy="12" r="1.5" fill={color}/>
    </svg>
  )
}

const s = {
  page: { background: '#0a0a0a', color: '#f0ede8', fontFamily: "'DM Mono', monospace", minHeight: '100vh' },
  nav: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 40px', borderBottom: '1px solid rgba(255,255,255,0.06)',
    position: 'sticky', top: 0, background: 'rgba(10,10,10,0.95)', backdropFilter: 'blur(12px)',
    zIndex: 10,
  },
  navLogo: { display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none' },
  navIcon: { width: 30, height: 30, background: '#c8a96e', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  navName: { fontFamily: "'Playfair Display', serif", fontSize: 15, fontWeight: 700, color: '#f0ede8' },
  navSub: { fontSize: 9, color: '#c8a96e', letterSpacing: '0.12em', textTransform: 'uppercase' },
  backLink: { fontSize: 12, color: '#888', textDecoration: 'none', fontFamily: "'DM Mono', monospace", transition: 'color 0.2s' },

  main: { maxWidth: 760, margin: '0 auto', padding: '60px 24px 80px' },
  header: { marginBottom: 50, paddingBottom: 30, borderBottom: '1px solid rgba(255,255,255,0.07)' },
  eyebrow: { fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#c8a96e', marginBottom: 14 },
  title: { fontFamily: "'Playfair Display', serif", fontSize: 'clamp(34px, 6vw, 54px)', fontWeight: 900, lineHeight: 1.1, color: '#f0ede8', marginBottom: 18 },
  meta: { fontSize: 11, color: '#666', letterSpacing: '0.04em' },

  sect: { marginBottom: 38 },
  h2: { fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, color: '#f0ede8', marginBottom: 14, letterSpacing: '0.01em' },
  sectBody: { fontSize: 13, lineHeight: 1.8, color: '#aaa' },
  ul: { marginTop: 10, marginBottom: 10, paddingLeft: 22, display: 'flex', flexDirection: 'column', gap: 8 },
  inlineLink: { color: '#c8a96e', textDecoration: 'none', borderBottom: '1px solid rgba(200,169,110,0.3)', transition: 'color 0.2s' },
  warn: {
    background: 'rgba(224,92,92,0.06)',
    border: '1px solid rgba(224,92,92,0.2)',
    borderLeft: '3px solid #e05c5c',
    padding: '14px 18px',
    borderRadius: 6,
    marginBottom: 14,
    color: '#d8d4cc',
  },
}
